import { Connection, PublicKey, Transaction } from "@solana/web3.js";
import { TOKEN_PROGRAM_ID } from "@solana/spl-token";
import { useEffect, useState } from "react";
import { useConnection } from "@solana/wallet-adapter-react";
import * as nicknameGen from "@axvdex/solana/nicknameAPI/js/src/generated";
import {
  Payment,
  SetNicknameInstructionAccounts,
  SetNicknameInstructionArgs,
  createSetNicknameInstruction,
} from "@axvdex/solana/nicknameAPI/js/src/generated";
import { useAppSelector } from "@axvdex/state";
import { selectWalletInfo } from "@axvdex/state/wallet/walletSelectors";
import { NicknameInfo } from "@axvdex/types/solana/nickname";
import { deriveCorrectActionCostsPDA, deriveCorrectConfigPDA } from "./utils/DeriveNicknamePDA";
import { verifySolanaClientOrThrow } from "./utils/client";

// CONST ======================================================================
const SOLANA_NICKNAME_PROGRAM_ID = process.env.REACT_APP_SOLANA_NICKNAME_PROGRAM_ID;
export const SOLANA_NICKNAME_PROGRAM_PUBLIC_KEY = new PublicKey(SOLANA_NICKNAME_PROGRAM_ID);
const SOLANA_NICKNAME_OWNER = process.env.REACT_APP_SOLANA_NICKNAME_OWNER;
export const SOLANA_NICKNAME_OWNER_PUBLIC_KEY = new PublicKey(SOLANA_NICKNAME_OWNER);
const SOLANA_NICKNAME_ACCOUNT_CONFIG = process.env.REACT_APP_SOLANA_NICKNAME_ACCOUNT_CONFIG;
export const SOLANA_NICKNAME_ACCOUNT_CONFIG_PUBLIC_KEY = new PublicKey(SOLANA_NICKNAME_ACCOUNT_CONFIG);
export const SOL_PUBLIC_KEY_STR = "9Bi4Z4Jv3Z5wWxjVVd7yg2y4jc8ADZAPmtZLEuZk4pDn";
export const SOL_PUBLIC_KEY = new PublicKey(SOL_PUBLIC_KEY_STR);

// HOOKS ======================================================================

export function useNicknameMap(subscribe = true) {
  const [nicknames, setNicknames] = useState<Map<string, NicknameInfo>>(new Map());
  const { connection } = useConnection();
  const walletInfo = useAppSelector(selectWalletInfo);

  const refresh = async () => {
    const nicknamesStore = await fetchSolanaNicknamesMap(connection);
    // Add if a nickname has changed another one
    // TODO: History - Not able to keep this field through time | will need a pub sub model like the MessageActivityHistory
    const nicknamesMap = new Map<string, NicknameInfo>();
    for (const [pubkey, nicknameInfo] of nicknamesStore) {
      nicknamesMap.set(pubkey.toBase58(), {
        ...nicknameInfo,
        hasChangedSomeoneElse: false,
        serialize: nicknameInfo.serialize,
        pretty: nicknameInfo.pretty,
      });
    }
    nicknamesMap.forEach((nicknameInfo, pubkey) => {
      if (nicknameInfo.changedBy.toBase58() !== pubkey && nicknamesMap.has(nicknameInfo.changedBy.toBase58())) {
        nicknamesMap.get(nicknameInfo.changedBy.toBase58())!.hasChangedSomeoneElse = true;
      }
    });

    setNicknames(nicknamesMap);
  };

  const earlySuccessSetNickname = (targetPublicKey: PublicKey, nickname: string) => {
    // No undo as for now
    let targetNicknameInfo = nicknames.get(targetPublicKey.toBase58());
    if (!walletInfo.isConnected || !targetNicknameInfo) {
      return;
    }
    targetNicknameInfo = {
      nickname: nickname,
      changedAt: Date.now(),
      changedBy: new PublicKey(walletInfo.walletAddress),
      ...targetNicknameInfo,
      serialize: targetNicknameInfo.serialize,
      pretty: targetNicknameInfo.pretty,
    };
    nicknames.set(targetPublicKey.toBase58(), targetNicknameInfo);
  };

  useEffect(() => {
    // Init the nickname map
    refresh();

    if (!subscribe) {
      // Subscribe to the nickname account
      const subscriptionId = connection.onAccountChange(SOLANA_NICKNAME_ACCOUNT_CONFIG_PUBLIC_KEY, async () => {
        console.log("Nickname map account changed | Updating nicknames ...");
        refresh();
      });
      // Unsubscribe from the sale when the component unmounts
      return () => {
        connection.removeAccountChangeListener(subscriptionId);
      };
    }
  }, [connection]);

  return { nicknames, earlySuccessSetNickname };
}

export function useUserBalances() {
  const walletInfo = useAppSelector(selectWalletInfo);
  const [balances, setBalances] = useState(new Map());
  const { connection } = useConnection();
  useEffect(() => {
    const fetch = async () => {
      if (walletInfo.isConnected) {
        const userWalletAddress = new PublicKey(walletInfo.walletAddress);
        const balances = new Map();
        const tokenAccounts = await connection.getParsedTokenAccountsByOwner(userWalletAddress, {
          programId: TOKEN_PROGRAM_ID,
        });

        const userSolanaBalance = await connection.getBalance(userWalletAddress);
        // Extract and sum the balances from these token accounts
        tokenAccounts.value.forEach(accountInfo => {
          const accountData = accountInfo.account.data.parsed.info;
          const tokenBalance = accountData.tokenAmount.uiAmount;
          const tokenMint = accountData.mint;
          balances.set(tokenMint, tokenBalance);
        });
        balances.set(SOL_PUBLIC_KEY_STR, userSolanaBalance);
        setBalances(balances);
      }
    };
    fetch();
  }, [walletInfo.walletAddress]);

  return balances;
}

// REQUESTS ===================================================================

export async function requestAddProtectionSolana(
  connection: Connection,
  targetPublicKey: PublicKey,
  payment: nicknameGen.Payment,
  usersPaymentAccount: PublicKey
) {
  const getProtectionRequest = mkSolanaProtectionRequest(targetPublicKey, payment, usersPaymentAccount);
  // await sendWithPhantomAndDebugTransaction(getProtectionRequest, connection, nicknameGen.errorFromCode);
  const signature = await window.solanaClient.sendTransaction(getProtectionRequest, connection);
  return signature;
}

export async function requestUpdateNicknameSolana(
  connection: Connection,
  targetPublicKey: PublicKey,
  nickname: string,
  payment: nicknameGen.Payment,
  usersPaymentAccount: PublicKey
) {
  const nicknameRequest = mkSolanaNicknameRequest(targetPublicKey, nickname, payment, usersPaymentAccount);
  // await sendWithPhantomAndDebugTransaction(nicknameRequest, connection, nicknameGen.errorFromCode);
  const signature = await window.solanaClient.sendTransaction(nicknameRequest, connection);
  return signature;
}

export async function fetchSolanaFeesMap(connection: Connection): Promise<nicknameGen.FeesStore | null> {
  const [feesAccount] = deriveCorrectActionCostsPDA(SOLANA_NICKNAME_ACCOUNT_CONFIG_PUBLIC_KEY);
  const feesAccountInfo = await connection.getAccountInfo(feesAccount);
  if (!feesAccountInfo) {
    console.error("[ERROR] No fees account found");
    return null;
  }
  const [fees] = nicknameGen.FeesStore.fromAccountInfo(feesAccountInfo);
  return fees;
}

export async function fetchSolanaNicknamesMap(
  connection: Connection
): Promise<Map<PublicKey, nicknameGen.NicknameInfo> | null> {
  const nicknameAccountInfo = await connection.getAccountInfo(SOLANA_NICKNAME_ACCOUNT_CONFIG_PUBLIC_KEY);
  if (!nicknameAccountInfo) {
    console.error("[ERROR] No nickname account found");
    return new Map();
  }
  const [store] = nicknameGen.NicknameStore.fromAccountInfo(nicknameAccountInfo);
  return store.nicknames;
}

// HELPERS ===================================================================

export function mkSolanaNicknameRequest(
  targetPublicKey: PublicKey,
  nickname: string,
  payment: Payment,
  usersPaymentAccount: PublicKey
): Transaction {
  verifySolanaClientOrThrow();
  // I will need to set up a Map of Map<MintKey, (User|Owner)Key> to run this
  // A correct implementation means that a lookup with SolanaPublicKey results in ownerTokenAccount
  // such that ownerTokenAccount == ownerAccount
  const [feesAccount] = deriveCorrectActionCostsPDA(SOLANA_NICKNAME_ACCOUNT_CONFIG_PUBLIC_KEY);
  const [configAccount] = deriveCorrectConfigPDA(SOLANA_NICKNAME_ACCOUNT_CONFIG_PUBLIC_KEY);

  const setNicknameAccounts: SetNicknameInstructionAccounts = {
    nicknameAccount: SOLANA_NICKNAME_ACCOUNT_CONFIG_PUBLIC_KEY,
    feesAccount: feesAccount,
    configAccount: configAccount,
    owner: SOLANA_NICKNAME_OWNER_PUBLIC_KEY,
    ownerTokenAccount: SOLANA_NICKNAME_OWNER_PUBLIC_KEY,
    sender: window.solanaClient.publicKey,
    tokenAccountDeductFrom: usersPaymentAccount,
    mintAccount: payment.mintAddress,
    tokenProgramAccount: TOKEN_PROGRAM_ID,
  };

  const setNicknameInstruction: SetNicknameInstructionArgs = {
    setNicknameArgs: {
      newNickname: nickname,
      targetPubkey: targetPublicKey,
      payment: payment,
    },
  };

  const setNickname = createSetNicknameInstruction(setNicknameAccounts, setNicknameInstruction);
  return new Transaction().add(setNickname);
}

function mkSolanaProtectionRequest(
  targetPublicKey: PublicKey,
  payment: nicknameGen.Payment,
  usersPaymentAccount: PublicKey
): Transaction {
  const [feesAccount] = deriveCorrectActionCostsPDA(SOLANA_NICKNAME_ACCOUNT_CONFIG_PUBLIC_KEY);
  const [configAccount] = deriveCorrectConfigPDA(SOLANA_NICKNAME_ACCOUNT_CONFIG_PUBLIC_KEY);

  const protectionRequiredAccounts: nicknameGen.SetProtectionInstructionAccounts = {
    nicknameAccount: SOLANA_NICKNAME_ACCOUNT_CONFIG_PUBLIC_KEY,
    feesAccount: feesAccount,
    configAccount: configAccount,
    owner: SOLANA_NICKNAME_OWNER_PUBLIC_KEY,
    ownerTokenAccount: SOLANA_NICKNAME_OWNER_PUBLIC_KEY,
    sender: targetPublicKey,
    tokenAccountDeductFrom: usersPaymentAccount,
    mintAccount: payment.mintAddress,
    tokenProgramAccount: TOKEN_PROGRAM_ID,
  };

  const argumentsForProtection: nicknameGen.SetProtectionInstructionArgs = {
    setProtectionArgs: {
      protection: true,
      payment: payment,
    },
  };

  const applyProtection = nicknameGen.createSetProtectionInstruction(
    protectionRequiredAccounts,
    argumentsForProtection
  );

  return new Transaction().add(applyProtection);
}
